export const SITE = {
  legalName: 'Mahageeta Technology Pvt Ltd',
  shortName: 'Mahageeta Technology',
  domain: 'mahageetatechnology.com',
  brandTagline: "Building Tomorrow's Digital Businesses",
  positioning: 'Enterprise software & AI engineering',
  subpositioning: 'Products for IoT, telecom, and network operations',
  tagline: 'SutraMQ · SutraACS · MGOpen NMS · eGrahmantree',
  description:
    'Mahageeta Technology builds SutraMQ (MQTT), SutraACS (TR-069), MGOpen NMS, and eGrahmantree — enterprise software engineered in India, with AI-led custom development on AWS, GCP, and Azure.',
  email: 'info@grahmantree.com',
  emailProducts: 'info@grahmantree.com',
  phone: '+91-6350347147',
  phoneDisplay: '+91 63503 47147',
  phoneTel: '+916350347147',
  whatsappUrl: 'https://wa.me/916350347147',
  location: 'Jaipur, Rajasthan, India',
  address: 'Jaipur, Rajasthan, India',
  handcraftedTagline: 'Proudly handcrafted in India',
  businessHours: 'Mon–Fri: 9 AM – 6 PM IST',
  yearFounded: '2024',
};

export const SITE_URL =
  import.meta.env.VITE_SITE_URL || 'https://mahageetatechnology.com';

export const EGRAHMANTREE_URL =
  import.meta.env.VITE_EGRAHMANTREE_URL || 'https://egrahmantree.com';

export const CONTACT_API_URL =
  import.meta.env.VITE_CONTACT_API_URL || 'https://api.grahmantree.com/api/contact/send-email';

/** Bump when hero assets change (cache bust) */
const HERO_ASSET_V = '6';

export const HERO_SLIDES = [
  {
    id: 'products',
    src: `/images/hero/software-dev.svg?v=${HERO_ASSET_V}`,
    alt: 'Mahageeta Technology product portfolio',
    caption: 'Enterprise product portfolio',
  },
  {
    id: 'sutramq',
    src: `/images/hero/sutramq.svg?v=${HERO_ASSET_V}`,
    alt: 'SutraMQ MQTT broker',
    caption: 'SutraMQ · MQTT at scale',
  },
  {
    id: 'sutraacs',
    src: `/images/hero/sutraacs.svg?v=${HERO_ASSET_V}`,
    alt: 'SutraACS TR-069 platform',
    caption: 'SutraACS · CPE fleet management',
  },
  {
    id: 'mgopennms',
    src: `/images/hero/mgopennms.svg?v=${HERO_ASSET_V}`,
    alt: 'MGOpen NMS platform',
    caption: 'MGOpen NMS · Unified network ops',
  },
  {
    id: 'egrahmantree',
    src: `/images/hero/egrahmantree.svg?v=${HERO_ASSET_V}`,
    alt: 'eGrahmantree home services',
    caption: 'eGrahmantree · Live in Jaipur',
  },
  {
    id: 'ai-cloud',
    src: `/images/hero/cloud.svg?v=${HERO_ASSET_V}`,
    alt: 'AI and multi-cloud engineering',
    caption: 'AI · AWS · GCP · Azure',
  },
];

export const HERO_PILLS = [
  'SutraMQ',
  'SutraACS',
  'MGOpen NMS',
  'eGrahmantree',
  'AI-led engineering',
  'AWS · GCP · Azure',
];

export const NAV_LINKS = [
  { label: 'Home', path: '/' },
  { label: 'Products', path: '/products' },
  { label: 'About', path: '/about' },
  { label: 'Demo', path: '/demo' },
  { label: 'Contact', path: '/contact' },
];

export const NAV_PRODUCT_LINKS = [
  { label: 'SutraMQ', path: '/products/sutramq', description: 'Enterprise MQTT broker' },
  { label: 'SutraACS', path: '/products/sutraacs', description: 'TR-069 CPE management' },
  { label: 'MGOpen NMS', path: '/products/mgopennms', description: 'Network operations' },
];

export const PRODUCTS = [
  {
    id: 'egrahmantree',
    name: 'eGrahmantree',
    category: 'Consumer platform',
    isFlagship: true,
    tagline: 'Trusted home services in Jaipur',
    description:
      'Live marketplace at egrahmantree.com — customers book verified home-service professionals with payments, subscriptions, referrals, and end-to-end field operations.',
    highlights: ['Mobile app', 'Web platform', 'Field teams', 'Admin ops', 'Live in Jaipur'],
    accent: '#ea580c',
    url: EGRAHMANTREE_URL,
    ctaLabel: 'Visit egrahmantree.com',
    ctaExternal: true,
    demoAvailable: false,
    heroImage: `/images/hero/egrahmantree.svg?v=${HERO_ASSET_V}`,
  },
  {
    id: 'sutramq',
    name: 'SutraMQ',
    category: 'IoT & messaging',
    tagline: 'Enterprise MQTT broker for scale and sovereignty',
    description:
      'High-performance MQTT broker engineered in Rust — 1M+ connections, MQTT 3.1.1 & 5.0, clustering, TLS/mTLS, built-in PII scanning, regional compliance, and Prometheus observability.',
    highlights: ['MQTT 5.0', 'Clustering', 'PII scanning', 'mTLS', 'Prometheus'],
    accent: '#3b82f6',
    url: null,
    ctaLabel: 'Request a demo',
    ctaExternal: false,
    demoAvailable: true,
    pagePath: '/products/sutramq',
    heroImage: `/images/hero/sutramq.svg?v=${HERO_ASSET_V}`,
    challenge: 'Connected device fleets need messaging that scales without losing control.',
    outcome: 'Sovereign MQTT infrastructure — clustering, mTLS, compliance, and 1M+ connections.',
  },
  {
    id: 'sutraacs',
    name: 'SutraACS',
    category: 'Telecom & CPE',
    tagline: 'TR-069 ACS built for millions of devices',
    description:
      'Auto Configuration Server for CPE fleet management — TR-069/CWMP protocol server, firmware provisioning, northbound REST API, and operator portal for ISPs and OEMs.',
    highlights: ['TR-069 / CWMP', 'Firmware push', 'Operator portal', 'Provisioning', 'Fleet scale'],
    accent: '#10b981',
    url: null,
    ctaLabel: 'Request a demo',
    ctaExternal: false,
    demoAvailable: true,
    pagePath: '/products/sutraacs',
    heroImage: `/images/hero/sutraacs.svg?v=${HERO_ASSET_V}`,
    challenge: 'ISPs and OEMs must provision, update, and manage CPE at fleet scale.',
    outcome: 'TR-069 ACS with firmware push, operator portal, and northbound APIs.',
  },
  {
    id: 'mgopennms',
    name: 'MGOpen NMS',
    category: 'Network operations',
    tagline: 'Unified network & infrastructure management',
    description:
      'Full-stack NMS for networking, telecom, OEMs, and MSPs — device inventory, topology, alerts, config backup, AI ops assistant, reports, and mobile apps for field teams.',
    highlights: ['Multi-protocol', 'Topology map', 'AI Ops', 'Config backup', 'Mobile apps'],
    accent: '#8b5cf6',
    url: null,
    ctaLabel: 'Request a demo',
    ctaExternal: false,
    demoAvailable: true,
    pagePath: '/products/mgopennms',
    heroImage: `/images/hero/mgopennms.svg?v=${HERO_ASSET_V}`,
    challenge: 'Network teams juggle inventory, alerts, topology, and field ops across siloed tools.',
    outcome: 'Unified NMS — monitoring, config backup, AI ops, and mobile apps in one platform.',
  },
];

export const FLAGSHIP = PRODUCTS.find((p) => p.isFlagship);

export const STATS = [
  { value: '4', label: 'Product lines', accent: '#2563eb' },
  { value: 'MQTT · ACS · NMS', label: 'Enterprise stack', accent: '#059669' },
  { value: 'Multi-cloud', label: 'AWS · GCP · Azure', accent: '#0284c7' },
  { value: 'India', label: 'Engineered & operated', accent: '#64748b' },
];

export const INDUSTRIES = [
  {
    id: 'iot',
    title: 'IoT & industrial',
    description:
      'Device telemetry, edge messaging, and fleet connectivity — MQTT infrastructure engineered for scale and compliance.',
    product: 'SutraMQ',
  },
  {
    id: 'telecom',
    title: 'Telecom & ISPs',
    description:
      'CPE provisioning, firmware distribution, and subscriber equipment management for operators and OEMs.',
    product: 'SutraACS',
  },
  {
    id: 'network',
    title: 'Network operations',
    description:
      'Unified monitoring, topology, alerts, and field operations for MSPs, OEMs, and enterprise IT teams.',
    product: 'MGOpen NMS',
  },
  {
    id: 'consumer',
    title: 'Consumer services',
    description:
      'Live marketplace operations — bookings, payments, field teams, and admin ops running in Jaipur today.',
    product: 'eGrahmantree',
  },
];

export const PRODUCTION_PROOF = [
  {
    id: 'live',
    label: 'Live operations',
    detail: 'eGrahmantree marketplace running in Jaipur — bookings, payments, and field teams daily.',
  },
  {
    id: 'enterprise',
    label: 'Enterprise-ready',
    detail: 'SutraMQ, SutraACS, and MGOpen NMS available for tailored product demos.',
  },
  {
    id: 'engineering',
    label: 'Production engineering',
    detail: 'Rust, Java/Spring, and React — the same stack we use on our own platforms.',
  },
  {
    id: 'cloud',
    label: 'Multi-cloud',
    detail: 'Design, deploy, and operate on AWS, Google Cloud, or Microsoft Azure.',
  },
];

export const WHY_MAHAGEETA = [
  {
    title: 'We operate what we build',
    description:
      'Our consumer platform runs in production every day. Enterprise buyers get the same engineering discipline — not outsourced slide decks.',
  },
  {
    title: 'Built for operator workloads',
    description:
      'MQTT at scale, TR-069 fleet management, and unified NMS — platforms shaped by real infra and telecom requirements.',
  },
  {
    title: 'Talk to the product team',
    description:
      'Demos and engagements go directly to the engineers who design, ship, and support the software.',
  },
];

export const DEMO_PRODUCT_OPTIONS = [
  { value: 'sutramq', label: 'SutraMQ — MQTT broker' },
  { value: 'sutraacs', label: 'SutraACS — TR-069 ACS' },
  { value: 'mgopennms', label: 'MGOpen NMS — Network management' },
  { value: 'egrahmantree', label: 'eGrahmantree — Home services platform' },
  { value: 'custom', label: 'Custom AI / cloud software' },
];

export const AI_SERVICES = [
  {
    id: 'ai',
    title: 'AI',
    subtitle: 'Applied intelligence',
    description:
      'Classical AI and machine learning woven into your product — predictions, classification, document AI, anomaly detection, and intelligent automation on your data.',
    highlights: ['ML models', 'MLOps', 'Intelligent workflows'],
  },
  {
    id: 'genai',
    title: 'GenAI',
    subtitle: 'Generative AI',
    description:
      'Production GenAI with large language models — customer copilots, knowledge assistants, content generation, and RAG over your docs with safety guardrails.',
    highlights: ['LLM applications', 'RAG', 'Fine-tuning & evals'],
  },
  {
    id: 'agents',
    title: 'Agentic AI',
    subtitle: 'Autonomous agents',
    description:
      'Agentic AI systems that reason, use tools, and complete multi-step tasks — support agents, ops copilots, and internal automation connected to your APIs and databases.',
    highlights: ['Tool-using agents', 'Multi-agent flows', 'Human oversight'],
  },
];

export const CLOUD_PLATFORMS = [
  {
    id: 'aws',
    name: 'Amazon Web Services',
    shortName: 'AWS',
    description:
      'Experts in AWS — Lambda, ECS/EKS, RDS, S3, SageMaker, and Bedrock for AI workloads at scale.',
  },
  {
    id: 'gcp',
    name: 'Google Cloud Platform',
    shortName: 'GCP',
    description:
      'Experts in GCP — Cloud Run, GKE, Vertex AI, and BigQuery for cloud-native and GenAI-heavy architectures.',
  },
  {
    id: 'azure',
    name: 'Microsoft Azure',
    shortName: 'Azure',
    description:
      'Experts in Azure — App Service, Azure OpenAI, Kubernetes Service, and enterprise identity integrations.',
  },
];

export const CUSTOM_SOFTWARE = {
  label: 'Partner with us',
  title: 'AI-led custom development',
  tagline: 'Your roadmap, our AI-native engineering.',
  description:
    'We build and modernise digital products with an AI-first mindset — combining strong software engineering, agentic workflows, and the right cloud (AWS, GCP, or Azure) for your business.',
  offerings: [
    'AI-led web, mobile, and API development',
    'GenAI, LLMs, RAG, and agentic AI solutions',
    'Multi-cloud architecture & migration (AWS, GCP, Azure)',
    'DevOps, observability, and production support',
  ],
  ctaLabel: 'Request a demo',
  contactPath: '/demo',
};

export const CAPABILITIES = [
  {
    id: 'products',
    title: 'Product company',
    description:
      'We ship and operate our own platforms — from home services to MQTT, ACS, and NMS — not just client projects.',
  },
  {
    id: 'genai',
    title: 'GenAI & LLMs',
    description:
      'Copilots, chat, summarisation, and knowledge bases powered by modern LLMs with evaluation, guardrails, and cost control.',
  },
  {
    id: 'agents',
    title: 'Agentic AI',
    description:
      'Autonomous and semi-autonomous agents that plan, call tools, and integrate with your stack — customer service, ops, and internal automation.',
  },
  {
    id: 'cloud',
    title: 'Cloud platforms',
    description:
      'Certified-level expertise across all three major clouds — design, build, and run on AWS, Google Cloud, or Microsoft Azure.',
  },
  {
    id: 'engineering',
    title: 'Full-stack engineering',
    description:
      'React, React Native, Java/Spring, Rust, and robust APIs — the foundation under every product and client engagement.',
  },
  {
    id: 'custom',
    title: 'Client partnerships',
    description:
      'End-to-end engagements for startups and enterprises. Tell us your vision — we bring AI, cloud, and product execution.',
  },
];

export const VALUES = [
  {
    title: 'Product-first mindset',
    description:
      'We build, run, and iterate on our own software daily — eGrahmantree in Jaipur and enterprise platforms for telecom and IoT.',
  },
  {
    title: 'AI-native by default',
    description:
      'We think AI-first — ML, GenAI, and agentic AI are part of how we design systems, not an afterthought.',
  },
  {
    title: 'Multi-cloud experts',
    description:
      'Deep hands-on experience on AWS, GCP, and Azure — pick the platform that fits; we deploy and operate with confidence.',
  },
];

export function getProductById(id) {
  return PRODUCTS.find((p) => p.id === id);
}

export function productDemoPath(productId) {
  return `/demo?product=${productId}`;
}
